import { Component, OnInit, inject, input, viewChild } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Subject } from 'rxjs';

import { BarChartComponent } from '../bar-chart/bar-chart.component';
import { BaseComponent } from '../../base/base.component';
import { BarChartAxisType, BarChartDate, BarChartDatePeriod, IdDescription } from '../../../models';
import { MockChartService } from '../../../mock';
import { BaseChartService, FormatService, UtilityService } from '../../../services';
import { FormlyService } from '../../../services/component-services/formly.service';
import { BaseInlineFormComponent } from '../../base-inline-form/base-inline-form.component';

@Component({
  selector: 'app-bar-chart-date',
  templateUrl: './bar-chart-date.component.html',
  styleUrl: './bar-chart-date.component.scss',
  imports: [BaseInlineFormComponent, BarChartComponent],
})
export class BarChartDateComponent<T> extends BaseComponent implements OnInit {
  private readonly formatService = inject(FormatService);
  private readonly utilityService = inject(UtilityService);
  private readonly formlyService = inject(FormlyService);

  private readonly chart = viewChild(BarChartComponent);

  public readonly header = input('');
  public readonly xKey = input<string>();
  public readonly xFormat = input<BarChartAxisType>(BarChartAxisType.date);
  public readonly yKey = input<string>();
  public readonly yName = input<string>();
  public readonly xRotation = input(0);
  public readonly yFormat = input<BarChartAxisType>(BarChartAxisType.currency);
  public readonly yKeyRight = input<string>();
  public readonly yNameRight = input<string>();
  public readonly yFormatRight = input<BarChartAxisType>(BarChartAxisType.number);
  public readonly service = input<BaseChartService<T>>(new MockChartService());
  public readonly productId = input('');
  public readonly id = input('');
  public readonly barChartPeriod = input<BarChartDatePeriod>(BarChartDatePeriod.Week);

  readonly BarChartAxisType = BarChartAxisType;
  readonly BarChartDatePeriodOptions: IdDescription[] = [
    { id: BarChartDatePeriod.Week, description: $localize`Week` },
    { id: BarChartDatePeriod.Month, description: $localize`Month` },
    { id: BarChartDatePeriod.Year, description: $localize`Year` },
  ];

  protected data$ = new Subject<T[]>();
  protected fields: FormlyFieldConfig[] = [];
  protected model: BarChartDate = {} as BarChartDate;

  ngOnInit(): void {
    this.reset();
    this.getFields();
  }

  get xAxisName(): string {
    switch (this.model.period) {
      case BarChartDatePeriod.Year:
        return $localize`Month`;
      case BarChartDatePeriod.Month:
        return $localize`Day of the Month`;
      case BarChartDatePeriod.Week:
        return $localize`Day of the Week`;
      default:
        return undefined;
    }
  }

  getSubHeader(data: T[]): string {
    const date = data.length > 0 ? this.formatService.dateForPeriods(data[0][this.xKey()], this.model.period) : '';
    const count = this.utilityService.sumByPropertyName(data, this.yKeyRight());
    const total = this.utilityService.sumByPropertyName(data, this.yKey());

    return `${date} - ${this.chart().formatSummary(count, total)}`;
  }

  dateFormat(date: Date): string {
    return this.formatService.dateItemsForPeriod(date, this.model.period);
  }

  reset() {
    this.model = {
      date: new Date(),
      period: this.barChartPeriod(),
      productId: this.productId(),
      id: this.id(),
    };
    this.getData();
  }

  periodChanged(period: BarChartDatePeriod) {
    this.model.period = period;
    this.getData();
  }

  clicked(data: T) {
    console.log(data);
  }

  private getData() {
    this.service()
      .get(this.model)
      .pipe(this.untilDestroyed())
      .subscribe((data: T[]) => this.data$.next(data));
  }

  private updateDate(add: boolean) {
    const multiplier = add ? 1 : -1;
    switch (this.model.period) {
      case BarChartDatePeriod.Week:
        this.model.date.setDate(this.model.date.getDate() + 7 * multiplier);
        break;
      case BarChartDatePeriod.Month:
        this.model.date.setMonth(this.model.date.getMonth() + 1 * multiplier);
        break;
      case BarChartDatePeriod.Year:
        this.model.date.setFullYear(this.model.date.getFullYear() + 1 * multiplier);
        break;
    }

    this.getData();
  }

  private getFields() {
    this.fields = [
      this.formlyService.button($localize`Reset`, () => this.reset(), {
        btnClass: 'secondary',
        iconClass: 'fa fa-sync',
      }),
      this.formlyService.button('', () => this.updateDate(false), {
        btnClass: 'success',
        iconClass: 'fa fa-less-than',
      }),
      this.formlyService.button('', () => this.updateDate(true), {
        btnClass: 'success',
        iconClassRight: 'fa fa-greater-than',
      }),
      this.formlyService.select('period', '', this.BarChartDatePeriodOptions, {
        change: (field: FormlyFieldConfig, event) => this.periodChanged(event.value),
      }),
    ];
  }
}
